import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const SCOPE = ["interior", "exterior"];
const SURFACES = ["walls", "ceilings", "doors", "trims", "fences", "garage_doors", "roofing", "decks"];
const PROPERTY_TYPES = ["house", "townhouse", "apartment", "commercial"];

export default function PainterProfile() {
  const { refreshMe } = useOutletContext();
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const heroInput = useRef(null);
  const logoInput = useRef(null);

  useEffect(() => {
    api.get("/painter/profile")
      .then((r) => setProfile(r.data))
      .catch((e) => {
        // 404 = no painter_profile record yet (owner preview, brand-new business).
        // Render an empty editor rather than an error toast.
        if (e?.response?.status === 404) {
          setProfile({
            bio: "",
            trading_name: "",
            contact_email: "",
            contact_phone: "",
            website_url: "",
            service_scope: [],
            service_surfaces: [],
            property_types: [],
            team_size: null,
            years_in_business: null,
            published: false,
            logo_url: null,
            hero_image_url: null,
          });
        } else {
          toast.error(formatApiError(e));
        }
      });
  }, []);

  if (!profile) return <div data-testid="painter-profile" className="text-sm text-brand-muted">Loading…</div>;

  const setF = (k, v) => setProfile((p) => ({ ...p, [k]: v }));
  const toggle = (k, item) => setProfile((p) => {
    const list = p[k] || [];
    return { ...p, [k]: list.includes(item) ? list.filter((x) => x !== item) : [...list, item] };
  });

  const save = async () => {
    setBusy(true);
    try {
      const patch = {
        bio: profile.bio, trading_name: profile.trading_name,
        contact_email: profile.contact_email, contact_phone: profile.contact_phone,
        website_url: profile.website_url,
        service_scope: profile.service_scope,
        service_surfaces: profile.service_surfaces,
        property_types: profile.property_types,
        team_size: profile.team_size, years_in_business: profile.years_in_business,
        published: profile.published,
      };
      const r = await api.patch("/painter/profile", patch);
      setProfile(r.data);
      await refreshMe();
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const uploadImage = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/painter/profile/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProfile((p) => ({ ...p, [`${kind}_image_url`]: r.data[`${kind}_image_url`], logo_url: r.data.logo_url || p.logo_url }));
      await refreshMe();
      toast.success(`${kind === "hero" ? "Hero" : "Logo"} updated`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="painter-profile" className="space-y-8">
      <div>
        <div className="overline mb-2">Profile</div>
        <h1 className="font-serif text-3xl text-brand-ink mb-2">Public business profile</h1>
        <p className="text-sm text-brand-muted">These details appear on your ColourMatch Pro public listing.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-6">
        <ImageBox label="Hero image" url={profile.hero_image_url}
          onPick={() => heroInput.current?.click()} testId="hero" />
        <input ref={heroInput} type="file" hidden accept="image/jpeg,image/png,image/webp"
          onChange={(e) => uploadImage("hero", e.target.files?.[0])} data-testid="hero-file" />
        <ImageBox label="Logo" url={profile.logo_url}
          onPick={() => logoInput.current?.click()} testId="logo" />
        <input ref={logoInput} type="file" hidden accept="image/jpeg,image/png,image/webp"
          onChange={(e) => uploadImage("logo", e.target.files?.[0])} data-testid="logo-file" />
      </div>

      <Field label="Trading name" id="trading_name" value={profile.trading_name || ""} onChange={(v) => setF("trading_name", v)} />
      <Textarea label="Bio (max 2000 chars)" id="bio" value={profile.bio || ""} onChange={(v) => setF("bio", v)} />

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Contact email" id="contact_email" type="email" value={profile.contact_email || ""} onChange={(v) => setF("contact_email", v)} />
        <Field label="Contact phone" id="contact_phone" value={profile.contact_phone || ""} onChange={(v) => setF("contact_phone", v)} />
      </div>
      <Field label="Website" id="website_url" value={profile.website_url || ""} onChange={(v) => setF("website_url", v)} />

      <Chips label="Scope" id="service_scope" options={SCOPE} selected={profile.service_scope || []} onToggle={(v) => toggle("service_scope", v)} />
      <Chips label="Surfaces" id="service_surfaces" options={SURFACES} selected={profile.service_surfaces || []} onToggle={(v) => toggle("service_surfaces", v)} />
      <Chips label="Property types" id="property_types" options={PROPERTY_TYPES} selected={profile.property_types || []} onToggle={(v) => toggle("property_types", v)} />

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Team size" id="team_size" type="number" value={profile.team_size || ""} onChange={(v) => setF("team_size", Number(v) || null)} />
        <Field label="Years in business" id="years_in_business" type="number" value={profile.years_in_business || ""} onChange={(v) => setF("years_in_business", Number(v) || null)} />
      </div>

      <label className="flex items-center gap-3 border-t border-brand-linen pt-6">
        <input type="checkbox" checked={!!profile.published} onChange={(e) => setF("published", e.target.checked)} data-testid="field-published" />
        <span className="text-sm text-brand-ink">My listing is publicly visible in the ColourMatch Pro directory</span>
      </label>

      <button onClick={save} disabled={busy} data-testid="save-profile" className="px-6 py-3 bg-brand-navy text-white text-sm disabled:opacity-50">
        {busy ? "Saving…" : "Save profile"}
      </button>
    </div>
  );
}

function ImageBox({ label, url, onPick, testId }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      <div className="border border-brand-linen bg-white aspect-[4/3] flex items-center justify-center overflow-hidden">
        {url ? (
          <img src={url} alt={label} className="w-full h-full object-cover" data-testid={`${testId}-preview`} />
        ) : (
          <div className="text-xs text-brand-muted">No image yet</div>
        )}
      </div>
      <button type="button" onClick={onPick} data-testid={`upload-${testId}`}
        className="mt-2 px-3 py-1.5 border border-brand-linen text-xs hover:bg-brand-linen/30">
        Upload {label.toLowerCase()}
      </button>
    </div>
  );
}

function Field({ label, id, type = "text", value, onChange }) {
  return (
    <div>
      <label htmlFor={id} className="overline mb-1.5 block">{label}</label>
      <input id={id} data-testid={`field-${id}`} type={type} value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-brand-linen p-3 text-sm" />
    </div>
  );
}

function Textarea({ label, id, value, onChange }) {
  return (
    <div>
      <label htmlFor={id} className="overline mb-1.5 block">{label}</label>
      <textarea id={id} data-testid={`field-${id}`} value={value} rows={5}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-brand-linen p-3 text-sm font-sans" />
    </div>
  );
}

function Chips({ label, id, options, selected, onToggle }) {
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      <div className="flex flex-wrap gap-2" data-testid={`chips-${id}`}>
        {options.map((o) => {
          const on = selected.includes(o);
          return (
            <button key={o} type="button" onClick={() => onToggle(o)}
              data-testid={`chip-${id}-${o}`}
              className={`px-3 py-1.5 text-sm border ${on ? "bg-brand-navy text-white border-brand-teal" : "border-brand-linen text-brand-ink hover:bg-brand-linen/30"}`}>
              {o.replace("_", " ")}
            </button>
          );
        })}
      </div>
    </div>
  );
}
